import { useEffect, useState } from "react";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useBusy } from "../../context/BusyContext";
import AdminDataModal from "../components/AdminDataModal";

export default function ProductCategories() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { runBusy } = useBusy();
  const { showModal } = useMessageModal();
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_LYRAZONE_BASE_URL}/admin/categories`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((res) => setData(res.data));
  }, []);

  /* =========================
     FETCH CATEGORIES
  ========================= */

  const fetchCategories = async () => {
    try {
      const res = await runBusy(() =>
        fetch(`${BASE_URL}/admin/categories`, {
          credentials: "include",
        }),
      );

      const result = await res.json();

      if (result.success) {
        const freshData = result.data || [];

        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Categories Error:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* =========================
     ADD / UPDATE SUBMIT
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/categories`
          : `${BASE_URL}/admin/categories/${activeRow.id}`;

      const res = await runBusy(() =>
        fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formData),
        }),
      );

      const result = await res.json();

      if (!res.ok || result.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message:
            result.message ||
            (modalMode === "add"
              ? "Failed to create category."
              : "Failed to update category."),
          confirmText: "OK",
        });
        return;
      }

      await fetchCategories();

      setModalOpen(false);
      setActiveRow(null);

      showModal({
        type: "success",
        title: "Success",
        message:
          result.message ||
          (modalMode === "add"
            ? "Category created successfully."
            : "Category updated successfully."),
        confirmText: "OK",
      });
    } catch (error: any) {
      console.error("Submit Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.message ||
          (modalMode === "add"
            ? "Failed to create category."
            : "Failed to update category."),
        confirmText: "OK",
      });
    }
  };

  /* =========================
     DELETE (SOFT DELETE)
  ========================= */
  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Category",
      message: `Are you sure you want to delete "${row.category_name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/categories/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchCategories();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Product type deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  const productFields: FormField[] = [
    {
      key: "id",
      label: "ID",
      colSpan: 2,
    },
    {
      key: "category_name",
      label: "Name",
      colSpan: 2,
    },
    {
      key: "description",
      label: "Description",
      type: "textarea",
      colSpan: 2,
    },
    {
      key: "selling_market",
      label: "Selling Market",
      type: "select",
      options: [
        { label: "Online", value: "online" },
        { label: "Offline", value: "offline" },
      ],
      colSpan: 2,
    },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      colSpan: 2,
    },
  ];

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "category_name", label: "Name" },
    {
      key: "selling_market",
      label: "Selling Market",
      render: (row) => (
        <span
          className={
            row.selling_market === "offline" ? "badge-warning" : "badge-success"
          }
        >
          {row.selling_market === "offline" ? "Offline" : "Online"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Product Categories"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");

          setActiveRow({
            id: "",
            category_name: "",
            description: "",
            selling_market: "online",
            status: "active",
          });

          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="products_categories"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>
      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Product Category"
        fields={productFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={
          handleSubmit
          // (formData) => {
          // console.log("Submitted:", formData);
          // setModalOpen(false);
          // }
        }
      />
    </div>
  );
}
